import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import type { FollowUpRow } from '../event-manager-client';

function ReadOnlyField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="grid grid-cols-[140px_1fr] py-2 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 rounded-lg px-2 -mx-2 transition-colors">
      <span className="text-xs text-nightshade-500 dark:text-nightshade-400">
        {label}
      </span>
      <span className="text-sm text-nightshade-700 dark:text-nightshade-300 whitespace-pre-wrap">{value || '\u2014'}</span>
    </div>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 mb-3 mt-1">
      <h4 className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 dark:text-nightshade-400 whitespace-nowrap">{label}</h4>
      <div className="h-px flex-1 bg-nightshade-200 dark:bg-nightshade-700" />
    </div>
  );
}

function SfdcLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs text-blueberry-500 hover:text-blueberry-600 transition-colors"
    >
      <ExternalLink className="w-3 h-3" />
      {label}
    </a>
  );
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

interface FollowUpDetailPanelProps {
  row: FollowUpRow;
  onClose: () => void;
}

export function FollowUpDetailPanel({ row, onClose }: FollowUpDetailPanelProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  return (
    <div className={`flex-shrink-0 h-full overflow-hidden transition-[width] duration-200 ${visible ? 'w-[400px]' : 'w-0'}`}>
      <div className="w-[400px] h-full border-l border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900 flex flex-col">
        {/* Header */}
        <div className="flex items-start gap-3 p-4 border-b border-nightshade-200 dark:border-nightshade-700">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-100 truncate">
              {row.contact_name ?? 'Unknown'}
            </h3>
            {row.account_name && (
              <p className="text-xs text-nightshade-500 dark:text-nightshade-400 truncate mt-0.5">
                {row.account_name}
              </p>
            )}
            {/* SFDC Links */}
            <div className="flex items-center gap-3 mt-1.5">
              {row.contact_id && (
                <SfdcLink
                  href={`https://clay.lightning.force.com/lightning/r/Contact/${row.contact_id}/view`}
                  label="Contact"
                />
              )}
              {row.contact_account_id && (
                <SfdcLink
                  href={`https://clay.lightning.force.com/lightning/r/Account/${row.contact_account_id}/view`}
                  label="Account"
                />
              )}
              {row.contact_linkedin && (
                <a
                  href={row.contact_linkedin.startsWith('http') ? row.contact_linkedin : `https://linkedin.com/in/${row.contact_linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blueberry-500 hover:text-blueberry-600 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  LinkedIn
                </a>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors"
          >
            <X className="w-4 h-4 text-nightshade-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Contact & Account Info */}
          <div>
            <SectionHeader label="Contact & Account" />
            <div className="space-y-3">
              <ReadOnlyField label="Email" value={row.contact_email} />
              <ReadOnlyField label="Title" value={row.contact_title} />
              <ReadOnlyField label="Account Type" value={row.account_type} />
              <ReadOnlyField label="Sales Tier" value={row.sales_tier} />
              <ReadOnlyField label="Product Tier" value={row.product_tier} />
              {row.number_of_open_opps != null && (
                <ReadOnlyField label="Open Opps" value={String(row.number_of_open_opps)} />
              )}
              <ReadOnlyField label="GTME Owner" value={row.gtme_owner_name} />
              <ReadOnlyField label="GS Owner" value={row.gs_owner_name} />
              <ReadOnlyField label="ClayDR" value={row.claydr_name} />
            </div>
          </div>

          <hr className="border-nightshade-200 dark:border-nightshade-700" />

          {/* Dispatch Details */}
          <div>
            <SectionHeader label="Dispatch Details" />
            <div className="space-y-3">
              <ReadOnlyField label="Bucket" value={row.bucket} />
              <ReadOnlyField label="Channel" value={row.channel} />
              <ReadOnlyField label="Sender" value={row.sender_email} />
              <ReadOnlyField label="Subject" value={row.subject} />
              <ReadOnlyField label="Body" value={row.body} />
            </div>
          </div>

          <hr className="border-nightshade-200 dark:border-nightshade-700" />

          {/* Result */}
          <div>
            <SectionHeader label="Result" />
            <div className="space-y-3">
              <ReadOnlyField label="Dispatch Result" value={row.result} />
              <ReadOnlyField label="Result Status" value={row.result_status} />
              <ReadOnlyField label="Dispatched At" value={fmtDate(row.dispatched_at)} />
              <ReadOnlyField label="Result At" value={fmtDate(row.result_at)} />
              {row.result_data && Object.keys(row.result_data).length > 0 && (
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-400 dark:text-nightshade-500 mb-0.5">
                    Result Data
                  </label>
                  <pre className="text-xs text-nightshade-600 dark:text-nightshade-400 bg-nightshade-50 dark:bg-nightshade-800 rounded-lg p-2 overflow-x-auto max-h-48">
                    {JSON.stringify(row.result_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
